import { PAYMENT_METHOD_KEYS, paymentMethodDisplayNames } from "@hypexone/uicore";

/**
 * Maps payment method identifiers (internal + agpay v3 aliases) to CMS lang keys.
 * Fallback labels are used until keys are added to lang files.
 */
export const PAYMENT_METHOD_LANG_KEY_MAP: Record<string, string> = {
  BANK_TRANSFER: "bank",
  UPI: "upi",
  UPI_TRANSFER: "upi",
  CRYPTO_WALLET_TRANSFER: "crypto",
  CRYPTO_WALLET: "crypto",
  GPAY: "gpay",
  PHONEPE: "phonepe",
  PAYTM_WALLET: "paytm",
  NEFT: "neft",
  IMPS: "imps",
  RTGS: "rtgs",
  [PAYMENT_METHOD_KEYS.BANK_TRANSFER]: "bank",
  [PAYMENT_METHOD_KEYS.UPI]: "upi",
  [PAYMENT_METHOD_KEYS.CRYPTO_WALLET_TRANSFER]: "crypto",
};

export const PAYMENT_METHOD_FALLBACK_LABELS: Record<string, string> = {
  bank: "Bank",
  upi: "UPI",
  crypto: "Crypto",
  gpay: "GPay",
  phonepe: "PhonePe",
  paytm: "Paytm",
  neft: "NEFT",
  imps: "IMPS",
  rtgs: "RTGS",
};

function normalizeWithdrawMethodKey(method: string): string {
  return method === "UPI" ? "UPI_TRANSFER" : method;
}

export function getPaymentMethodLangKey(method: string): string {
  const normalized = normalizeWithdrawMethodKey(method);
  return (
    PAYMENT_METHOD_LANG_KEY_MAP[method] ??
    PAYMENT_METHOD_LANG_KEY_MAP[normalized] ??
    normalized.toLowerCase()
  );
}

export function getLocalizedPaymentMethodLabel(
  method: string,
  langData?: Record<string, string> | null,
  displayNameMap: Record<string, string> = paymentMethodDisplayNames as Record<
    string,
    string
  >
): string {
  const normalized = normalizeWithdrawMethodKey(method);
  const langKey = getPaymentMethodLangKey(method);
  const fromDisplay = displayNameMap[normalized];

  return (
    langData?.[langKey] ??
    PAYMENT_METHOD_FALLBACK_LABELS[langKey] ??
    fromDisplay ??
    langKey.replace(/_/g, " ")
  );
}

export function isBankPaymentMethod(paymentMethodName: string): boolean {
  return (
    paymentMethodName === "BANK_TRANSFER" ||
    paymentMethodName === PAYMENT_METHOD_KEYS.BANK_TRANSFER ||
    paymentMethodName === "method1"
  );
}

/** agpay v3 queue_source_details keys → CMS lang keys (UI labels unchanged for users). */
export const BANK_DETAIL_DISPLAY_FIELDS = [
  { key: "bah", langKey: "account_holder_name", fallback: "Account Holder Name" },
  { key: "ban", langKey: "account_no", fallback: "Account No" },
  { key: "icode", langKey: "ifsc_code", fallback: "IFSC Code" },
  { key: "bname", langKey: "bank_name", fallback: "Bank Name" },
] as const;

export function getBankDetailDisplayLabel(
  fieldKey: string,
  langData?: Record<string, string> | null
): string {
  const field = BANK_DETAIL_DISPLAY_FIELDS.find((f) => f.key === fieldKey);
  if (field) {
    return langData?.[field.langKey] ?? field.fallback;
  }
  const legacyLangKeys: Record<string, string> = {
    bank_account_holder: "account_holder_name",
    bank_account_number: "account_no",
    ifs_code: "ifsc_code",
    bank_name: "bank_name",
  };
  const langKey = legacyLangKeys[fieldKey];
  if (langKey) {
    const match = BANK_DETAIL_DISPLAY_FIELDS.find((f) => f.langKey === langKey);
    return langData?.[langKey] ?? match?.fallback ?? fieldKey.replace(/_/g, " ");
  }
  return fieldKey.replace(/_/g, " ");
}

/** Deposit gateway tab labels (internal names + method1/2/3 aliases). */
export function getDepositPaymentMethodLabel(
  paymentMethodName: string,
  langData?: Record<string, string> | null
): string {
  return getLocalizedPaymentMethodLabel(paymentMethodName, langData, {
    BANK_TRANSFER: "Bank",
    [PAYMENT_METHOD_KEYS.BANK_TRANSFER]: "Bank",
    UPI: "UPI",
    [PAYMENT_METHOD_KEYS.UPI]: "UPI",
    UPI_TRANSFER: "UPI",
    CRYPTO_WALLET_TRANSFER: "Crypto",
    [PAYMENT_METHOD_KEYS.CRYPTO_WALLET_TRANSFER]: "Crypto",
  });
}
